// pages/VoyageursTerrestre.jsx
import React, { useState } from 'react';
import { 
    Search, 
    FileDown,
    RefreshCw,
    ArrowUpRight,
    ArrowDownRight,
    Map,
    Bus
} from 'lucide-react';
import SidebarAdmin from './superdashboard/SideBarAdmin';
import MobileNavigationAdmin from './superdashboard/MobileNavigationAdmin';
import DashboardHeaderAdmin from './superdashboard/DashboardHeaderAdmin';
import useGlobale from '../components/global';
import AdminMenu from '../components/AdminMenu';

// Composant pour les cartes de statistiques
const StatCard = ({ title, value, change, icon: Icon, bgColor }) => (
    <div className={`bg-gradient-to-r ${bgColor} rounded-xl p-6 text-white shadow-lg`}>
        <div className="flex justify-between items-start">
            <div>
                <p className="text-white/80 text-sm">{title}</p>
                <h3 className="text-2xl font-bold mt-1">{value}</h3>
                <p className="text-white/80 text-sm mt-1">{change}</p>
            </div>
            <div className="bg-white/20 p-3 rounded-lg">
                <Icon className="w-6 h-6" />
            </div>
        </div>
    </div>
);

export default function VoyageurTerrestre() {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const { user } = useGlobale();
    const [filters, setFilters] = useState({
        type: 'tous', 
        poste: 'tous',
        dateDebut: '',
        dateFin: '',
        transport: 'tous'
    });

    // Statistiques pour les voyages terrestres
    const stats = [
        {
            title: 'Entrées Terrestres',
            value: '3,478',
            change: '+22% ce mois',
            icon: ArrowDownRight,
            bgColor: 'from-brand-500 to-brand-600'
        },
        {
            title: 'Sorties Terrestres',
            value: '2,895',
            change: '+15% ce mois',
            icon: ArrowUpRight,
            bgColor: 'from-green-600 to-green-700'
        },
        {
            title: 'Points de Contrôle Actifs',
            value: '12',
            change: 'Sur 15 points',
            icon: Map,
            bgColor: 'from-brand-500 to-brand-600'
        },
        {
            title: 'Transporteurs Enregistrés',
            value: '45',
            change: '8 en attente',
            icon: Bus,
            bgColor: 'from-green-600 to-green-700'
        }
    ];

    // Données de démonstration pour les voyages terrestres
    const voyageurs = [
        {
            id: 1,
            nom: "Moussa Ibrahim",
            passport: "N1234567",
            type: "Entrée",
            date: "2024-03-15",
            transport: "Bus SNTV",
            poste: "Gaya",
            provenance: "Cotonou",
            destination: "Niamey",
            genre: "Homme",
            nationalite: "Nigérien",
            status: "Validé"
        },
        {
            id: 2,
            nom: "Aisha Mohammed",
            passport: "N7654321",
            type: "Sortie",
            date: "2024-03-15",
            transport: "Rimbo",
            poste: "Maradi",
            provenance: "Niamey",
            destination: "Kano",
            genre: "Femme",
            nationalite: "Nigérienne",
            status: "En cours"
        },
        {
            id: 3,
            nom: "Ali Mahamane",
            passport: "N9876543",
            type: "Entrée",
            date: "2024-03-16",
            transport: "STM",
            poste: "Zinder",
            provenance: "Kano",
            destination: "Niamey",
            genre: "Homme",
            nationalite: "Nigérien",
            status: "Validé"
        },
        {
            id: 4,
            nom: "Fatima Souleymane",
            passport: "N5432109",
            type: "Sortie",
            date: "2024-03-16",
            transport: "Africa Trans",
            poste: "Gaya",
            provenance: "Niamey",
            destination: "Cotonou",
            genre: "Femme",
            nationalite: "Nigérienne",
            status: "En cours"
        }
    ];

    const handleExport = () => {
        console.log('Exporting data...');
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <SidebarAdmin
                isOpen={isSidebarOpen}
                menuItems={AdminMenu}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1">
                <DashboardHeaderAdmin
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />

                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8">
                    {/* En-tête */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center">
                            <div>
                                <h1 className="text-2xl font-bold text-[#437011]">Voyages Terrestres</h1>
                                <p className="text-gray-600">Gestion et suivi des voyages terrestres transfrontaliers</p>
                            </div>
                            <div className="flex space-x-3">
                                <button 
                                    onClick={handleExport}
                                    className="flex items-center px-4 py-2 bg-[#96B734] text-white rounded-lg hover:bg-[#437011] shadow-md hover:shadow-lg transition-all"
                                >
                                    <FileDown className="w-4 h-4 mr-2" />
                                    Exporter
                                </button>
                                <button 
                                    className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 shadow-md hover:shadow-lg transition-all"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Actualiser
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {stats.map((stat, index) => (
                            <StatCard key={index} {...stat} />
                        ))}
                    </div>

                    {/* Filtres */}
                    <div className="bg-white p-4 rounded-xl shadow-lg mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type de voyage</label>
                                <select 
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#96B734] focus:border-transparent"
                                    value={filters.type}
                                    onChange={(e) => setFilters({...filters, type: e.target.value})}
                                >
                                    <option value="tous">Tous</option>
                                    <option value="entree">Entrée</option>
                                    <option value="sortie">Sortie</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Poste Frontière</label>
                                <select 
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#96B734] focus:border-transparent"
                                    value={filters.poste}
                                    onChange={(e) => setFilters({...filters, poste: e.target.value})}
                                >
                                    <option value="tous">Tous les postes</option>
                                    <option value="gaya">Gaya</option>
                                    <option value="maradi">Maradi</option>
                                    <option value="zinder">Zinder</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Transporteur</label>
                                <select 
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#96B734] focus:border-transparent"
                                    value={filters.transport}
                                    onChange={(e) => setFilters({...filters, transport: e.target.value})}
                                >
                                    <option value="tous">Tous</option>
                                    <option value="sntv">SNTV</option>
                                    <option value="rimbo">Rimbo</option>
                                    <option value="stm">STM</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                <input 
                                    type="date" 
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#96B734] focus:border-transparent"
                                    value={filters.dateDebut}
                                    onChange={(e) => setFilters({...filters, dateDebut: e.target.value})}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Recherche</label>
                                <div className="relative">
                                    <input 
                                        type="text" 
                                        placeholder="Rechercher..."
                                        className="w-full border rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-[#96B734] focus:border-transparent"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Voyageur
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Transport & Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Poste Frontière
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Trajet
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {voyageurs.map((voyageur) => (
                                        <tr key={voyageur.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{voyageur.nom}</div>
                                                <div className="text-sm text-gray-500">{voyageur.passport}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    voyageur.type === 'Entrée' 
                                                        ? 'bg-[#96B734] text-white' 
                                                        : 'bg-brand-600 text-white'
                                                }`}>
                                                    {voyageur.type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">{voyageur.transport}</div>
                                                <div className="text-sm text-gray-500">{voyageur.date}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {voyageur.poste}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                {voyageur.type === 'Entrée' ? voyageur.provenance : voyageur.destination}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    voyageur.status === 'Validé' 
                                                        ? 'bg-[#96B734] text-white' 
                                                        : 'bg-yellow-500 text-white'
                                                }`}>
                                                    {voyageur.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                            <div className="flex justify-between items-center">
                                <div className="text-sm text-gray-700">
                                    Affichage de <span className="font-medium">1</span> à <span className="font-medium">10</span> sur{' '}
                                    <span className="font-medium">97</span> résultats
                                </div>
                                <div className="flex space-x-2">
                                    <button 
                                        className="px-3 py-1 border border-[#96B734] rounded-lg text-sm font-medium text-[#437011] hover:bg-[#F4F8E9] transition-colors"
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Précédent
                                    </button>
                                    <button 
                                        className="px-3 py-1 border border-[#96B734] rounded-lg text-sm font-medium text-[#437011] hover:bg-[#F4F8E9] transition-colors"
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                    >
                                        Suivant
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                <MobileNavigationAdmin menuItems={AdminMenu} />
            </div>
        </div>
    );
}